import { reactive, watch, computed , ref } from 'vue';
import apiservice from '@/services/apiservice.js';
import useFormValidation from '@/hooks/formValidation.js';
import { i18n } from '@/main.js';


export default function useCursoDisciplinaForm(hasDate = false, filters = null){

  const { t } = i18n.global; 
  const locale = ref(i18n.global.locale.value); 

  const initializeFilter = (filter) => filter != null ? {...filter} : null;
  const { ciclo, curso, ano, disciplina, turma, date } = filters || {};

  const initCiclo = initializeFilter(ciclo);
  const initCurso = initializeFilter(curso);
  const initAno = initializeFilter(ano);
  const initDisciplina = initializeFilter(disciplina);
  const initTurma = initializeFilter(turma);
  const initDate = date || null;

  //#region computed vars
  const availableCursos = computed(() => {
    const { value } = form.ciclo;
    const cursos = options.cursos;
    if (value != null){
      return [...cursos].filter(el => el.cicloz == value.value);
    }
    return [...cursos];
  });

  const availableUcs = computed(() => {
    const { curso, ano } = form;
    if (!curso.value) {
      return [];
    }
    const anoValue = ano.value?.value;
    return [...options.disciplinas]
      .filter(el => el.cursoid === curso.value.value)
      .filter(el => !anoValue || el.ano === anoValue);
  });

  const availableAnos = computed(() => {
    if(form.curso?.value == null){
      return [];
    }
    return [...options.anos].filter(el => el.value <= form.curso?.value.anos);
  });

  const availableTurmas = computed(() => {
    const { curso, ano } = form;
    if(form.curso?.value == null){
      return [];
    }
    const cursoId = curso.value.value;
    const anoId = ano.value?.value;
    return [...options.turmas].filter(el => 
      el.cursoid === cursoId && (anoId == null || el.ano === anoId)
    );
  });
  //#endregion

  const form = reactive({
    ...(hasDate ? {
      date: {
        label: computed(() => t('forms.data',locale.value)),
        required: true,
        type: "date",
        value: initDate,
        index: 0,
        errors:[],
        msgs:[]
      }
    } : {}),
    ciclo: {
      label: computed(() => t('forms.ciclo',locale.value)),
      required: true,
      type: "select",
      options: [],
      value: initCiclo,
      index: 0,
      errors:[],
      msgs:[]
    },
    curso: {
      label: computed(() => t('forms.curso',locale.value)),
      required: true,
      type: "select",
      options: availableCursos,
      value: initCurso,
      index: 3,
      errors:[],
      msgs:[]
    },
    ano: {
      label: computed(() => t('forms.ano',locale.value)),
      required: true,
      type: "select",
      options: availableAnos,
      value: initAno,
      index: 1,
      errors:[],
      msgs:[]
    },
    disciplina: {
      label: computed(() => t('forms.disciplina',locale.value)), 
      required: false,
      type: "select",
      options: availableUcs,
      value: initDisciplina,
      errors:[],
      msgs:[]
    },
    turma:{
      label:  computed(() => t('forms.turma',locale.value)),
      required: false,
      type: "select",
      options: availableTurmas,
      value: initTurma,
      errors:[],
      msgs:[]
    }
  });

  const options = reactive({
    ciclos: [],
    cursos: [],
    anos: [],
    disciplinas: [],
    turmas: [],
  });

  let fetching = ref(null);
  
  const LOADER_SHOW_DELAY = 150;

  let controller;

  const getOptions = (currentweek,currentLocal) => {
    if (controller) {
      controller.abort();
    }
    controller = new AbortController();
    const { signal } = controller;

    const timer = setTimeout(() => {
      fetching.value = true;
    },LOADER_SHOW_DELAY);
    
    const anos = [1, 2, 3, 4, 5].map(el => {
      return {
        label: `${el}º ${t('ano')}`,
        value: el,
        valor: el
      }
    });
    
    const options = {
      ano:anos,
      ciclo: [],
      curso: [],
      disciplina: [],
      turma: []
    }

    const promises = [
      apiservice.fetchCiclos(currentweek,currentLocal,signal).then(data => {
        options.ciclo = [...data];
      }),
      apiservice.fetchCursos(currentweek,currentLocal,signal).then(data => {
        options.curso = [...data];
      }),
      apiservice.fetchDisciplinas(currentweek,currentLocal,signal).then(data => {
        options.disciplina = [...data];
      }),
      apiservice.fetchTurmas(currentweek,currentLocal,signal).then(data => {
        options.turma = [...data];
      })
    ];

    return Promise.all(promises).then(() => {
      return options;
    }).catch(error => {
      fetching.value = false;
      clearTimeout(timer);
    });
  }

  const fetchOptions = (currentweek,currentLocal) => {

    if (controller) {
      controller.abort();
    }
    controller = new AbortController();
    const { signal } = controller;

    const timer = setTimeout(() => {
      fetching.value = true;
    },LOADER_SHOW_DELAY);
    
    options.anos = [1, 2, 3, 4, 5].map(el => {
      return {
        label: `${el}º ${t('ano')}`,
        value: el,
        valor: el
      }
    });
    
    const promises = [
      apiservice.fetchCiclos(currentweek,currentLocal,signal).then(data => {
        form.ciclo.options = data;
        options.ciclos = [...data];
      }),
      apiservice.fetchCursos(currentweek,currentLocal,signal).then(data => {
        options.cursos = data;
      }),
      apiservice.fetchDisciplinas(currentweek,currentLocal,signal).then(data => {
        options.disciplinas = data;
      }),
      apiservice.fetchTurmas(currentweek,currentLocal,signal).then(data => {
        options.turmas = data;
      })
    ];

    return Promise.all(promises).then(() => {
      fetching.value = false;
      clearTimeout(timer);
    }).catch(error => {
      fetching.value = false;
      clearTimeout(timer);
    });

  }

  const getSubmitData = (data,currentweek) => {
    const { validateForm } = useFormValidation();
    if(validateForm(form)){
      let selectedDates = data != null ? data : {
        "start": currentweek.start,
        "end": currentweek.end
      }
      const { ciclo, curso, ano, disciplina, turma } = form;
      let params = {
          ciclo: ciclo.value,
          ano: ano.value,
          curso: curso.value,
          disciplina: disciplina.value,
          turma: turma.value,
          datas: selectedDates
      };
      return [selectedDates,params,form];
    }
    return null;
  }


  //#region watchers
  
  const toggleNoResultsWarning = () => {
    if(availableUcs.value.length == 0){
      form.disciplina.msgs.push("Disciplina");
      form.turma.msgs.push("Turma");
    }else{
      form.disciplina.msgs = form.disciplina.msgs.filter(item => item != "Disciplina");
      form.turma.msgs = form.turma.msgs.filter(item => item != "Turma");
    }
  }

  watch(availableUcs, () => toggleNoResultsWarning());
  
  const isValueCleared = (newVal) => newVal === null
  const isUserTriggeredValueChange = (newVal,oldVal) => oldVal !== null && oldVal?.value !== newVal?.value

  watch(() => form.ciclo.value, (newVal, oldVal) => {
    if(isValueCleared(newVal) || isUserTriggeredValueChange(newVal,oldVal)) {
      resetCombosForCicloNullOrDifferentValue()
    }
  }, { immediate: false }); 
  
  watch(() => form.curso.value, (newVal, oldVal) => {
    if(isValueCleared(newVal)) {
      resetCombosForCursoNull()
    }else{
      setCicloIfCursoSelectedAndCicloNull(newVal)
      setYearIfOnlyOneAvailable(newVal)
    }
  }, { immediate: false }); 
  
  watch(() => form.ano.value, (newVal, oldVal) => {
    if(isValueCleared(newVal)) {
      resetCombosForAnoNull()
    }
  }, { immediate: false }); 
  
  //#endregion

  //#region reset values
  function setCicloIfCursoSelectedAndCicloNull(newVal){
    if(form.ciclo.value == null && newVal != null){
      form.ciclo.value = form.ciclo.options.find(el => el.valor == newVal.cicloz);
    }
  }

  function setYearIfOnlyOneAvailable(newVal){
    if(newVal?.anos == 1){
      form.ano.value = {...form.ano.options[0]};
    }
  }

  function resetCombos(combos) {
    combos.forEach(combo => {
      form[combo].value = null;
    });
  }

  function resetCombosForCicloNullOrDifferentValue() {
    resetCombos(['curso', 'ano', 'disciplina', 'turma']);
  }
  
  function resetCombosForCursoNull() {
    resetCombos(['ano', 'disciplina', 'turma']);
  }
  
  function resetCombosForAnoNull() {
    resetCombos(['disciplina', 'turma']);
  }

  //#endregion
  
  const checkFormAfterChanginDate = () => {
    if(form.disciplina.options.length == 0){
      form.disciplina.value = null;
    }
    if(form.turma.options.length == 0){
      form.turma.value = null;
    }
  }

  const setFormDate = (currentweek) => {
    if(form.date){
      form.date.value = currentweek;
    }
  }

  return {
    getOptions,
    form,
    fetchOptions,
    getSubmitData,
    fetching,
    setFormDate,
    checkFormAfterChanginDate
  }
}